import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send, Plus, Trash2, LogOut, LayoutDashboard, Menu, X, Loader2, Paperclip, FileText,
  Mic, Square, Volume2, Fingerprint,
} from "lucide-react";
import { startRegistration } from "@simplewebauthn/browser";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, API, uploadFile, formatApiError } from "../../lib/api";
import { AiBlock } from "../../components/os/AiBlocks";
import TenantSwitcher from "../../components/os/TenantSwitcher";
import ChatBell from "../../components/os/ChatBell";
import NotificationBell from "../../components/os/NotificationBell";
import DayStart from "../../components/os/DayStart";
import OnboardingGate from "../../components/os/OnboardingGate";

const ease = [0.16, 1, 0.3, 1];

const SUGGESTIONS = [
  { t: "Dashboard de tesorería", q: "Dame el dashboard de tesorería" },
  { t: "Pipeline del CRM", q: "¿Cómo va el pipeline del CRM?" },
  { t: "Facturas por cobrar", q: "Muéstrame las facturas pendientes de cobro" },
  { t: "Mi equipo", q: "¿Quiénes forman el equipo?" },
  { t: "Ingresos vs gastos", q: "Compárame los ingresos contra los gastos de los proyectos" },
  { t: "Crear un lead", q: "Quiero registrar un nuevo cliente potencial" },
];

function Orb() {
  return (
    <div className="relative w-24 h-24 mx-auto">
      <motion.div className="absolute inset-0 rounded-full"
        style={{ background: "conic-gradient(from 0deg, #6C5CE7, #C9A96A, #10B981, #6C5CE7)" }}
        animate={{ rotate: 360 }} transition={{ duration: 9, repeat: Infinity, ease: "linear" }} />
      <div className="absolute inset-[3px] rounded-full bg-cream" />
      <motion.div className="absolute inset-[14px] rounded-full"
        style={{ background: "radial-gradient(circle at 30% 30%, #8b7bff, #6C5CE7 55%, #3d2fae)" }}
        animate={{ scale: [1, 1.07, 1] }} transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="absolute inset-0 rounded-full blur-2xl opacity-30"
        style={{ background: "#6C5CE7" }}
        animate={{ scale: [1, 1.25, 1] }} transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }} />
    </div>
  );
}

function renderText(text) {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    const html = line
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (/^###\s/.test(line)) return <div key={i} className="mt-3 mb-1 font-medium text-navy" dangerouslySetInnerHTML={{ __html: html.replace(/^###\s/, "") }} />;
    if (/^[-•]\s/.test(line)) return <div key={i} className="flex gap-2 ml-1"><span className="text-[#6C5CE7] mt-[2px]">·</span><span dangerouslySetInnerHTML={{ __html: html.replace(/^[-•]\s/, "") }} /></div>;
    if (!line.trim()) return <div key={i} className="h-2" />;
    return <div key={i} dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

export default function OmniaChat() {
  const { user, logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [toolStatus, setToolStatus] = useState(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [hrReady, setHrReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const voiceModeRef = useRef(false);
  const audioRef = useRef(null);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setSpeakingIdx(null);
  };

  const speak = async (text, idx) => {
    if (!text) return;
    if (speakingIdx === idx) { stopAudio(); return; }
    stopAudio();
    setSpeakingIdx(idx);
    try {
      const token = localStorage.getItem("omnia_token");
      const res = await fetch(`${API}/ai/voice/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("tts");
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => setSpeakingIdx(null);
      await audio.play();
    } catch {
      setSpeakingIdx(null);
      toast.error("No pude reproducir el audio");
    }
  };

  const toggleRecording = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 1000) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voz.webm");
          const { data } = await api.post("/ai/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) {
            voiceModeRef.current = true;
            send(data.text);
          } else {
            toast.error("No entendí el audio, inténtalo de nuevo");
          }
        } catch {
          toast.error("No pude transcribir tu voz");
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Necesito permiso para usar el micrófono");
    }
  };

  const enrollPasskey = async () => {
    try {
      const origin = window.location.origin;
      const { data: options } = await api.post("/auth/passkey/register/begin", { origin });
      const credential = await startRegistration({ optionsJSON: options });
      await api.post("/auth/passkey/register/finish", { credential, origin });
      toast.success("Passkey registrada: ya puedes entrar con huella o Face ID");
    } catch (e) {
      if (e?.name !== "NotAllowedError")
        toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo registrar la passkey");
    }
  };
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const data = await uploadFile(f, true);
        setAttachments((a) => [...a, { path: data.path, filename: data.filename || f.name, content_type: data.content_type || f.type }]);
      } catch {
        /* archivo rechazado */
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const loadSessions = useCallback(() => {
    api.get("/ai/agent/sessions").then((r) => setSessions(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, toolStatus]);

  const openSession = async (sid) => {
    setSessionId(sid);
    setSideOpen(false);
    try {
      const r = await api.get(`/ai/agent/history/${sid}`);
      setMessages(r.data.map((m) => ({ role: m.role, content: m.content || "", blocks: m.blocks || [], attachments: m.attachments || [], director: m.director || null, council: m.council || null })));
    } catch { setMessages([]); }
  };

  const newSession = () => {
    setSessionId(crypto.randomUUID());
    setMessages([]);
    setSideOpen(false);
    inputRef.current?.focus();
  };

  const removeSession = async (sid, e) => {
    e.stopPropagation();
    await api.delete(`/ai/agent/sessions/${sid}`).catch(() => {});
    if (sid === sessionId) newSession();
    loadSessions();
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if ((!msg && !attachments.length) || streaming || uploading) return;
    const atts = [...attachments];
    setAttachments([]);
    setInput("");
    const finalMsg = msg || "Te adjunto estos archivos.";
    setMessages((m) => [...m, { role: "user", content: finalMsg, blocks: [], attachments: atts }, { role: "assistant", content: "", blocks: [] }]);
    setStreaming(true);
    setToolStatus(null);
    let fullText = "";
    let runId = null;
    let evCount = 0;
    let gotDone = false;
    const applyEvt = (evt) => {
      if (evt.run_id) { runId = evt.run_id; return; }
      if (evt.done) { gotDone = true; return; }
      if (evt.heartbeat) return;
      evCount += 1;
      if (evt.delta) {
        setToolStatus(null);
        fullText += evt.delta;
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, content: last.content + evt.delta };
          return copy;
        });
      } else if (evt.tool) {
        setToolStatus(evt.tool);
      } else if (evt.council) {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, council: evt.council };
          return copy;
        });
      } else if (evt.director) {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, director: evt.director };
          return copy;
        });
      } else if (evt.block) {
        setToolStatus(null);
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, blocks: [...last.blocks, evt.block] };
          return copy;
        });
      } else if (evt.error) {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, content: last.content || "No pude completar esta operación. Inténtalo de nuevo." };
          return copy;
        });
      }
    };
    try {
      const token = localStorage.getItem("omnia_token");
      const res = await fetch(`${API}/ai/agent`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: finalMsg, session_id: sessionId, attachments: atts }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line); } catch { continue; }
          applyEvt(evt);
        }
      }
      if (runId && !gotDone) {
        for (let p = 0; p < 240 && !gotDone; p++) {
          await new Promise((r) => setTimeout(r, 2500));
          try {
            const { data } = await api.get(`/ai/agent/council/${runId}?after=${evCount}`);
            for (const evt of data.events || []) applyEvt(evt);
            if (data.done || data.missing) gotDone = true;
          } catch { gotDone = true; }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Error de conexión. Inténtalo de nuevo.", blocks: [] };
        return copy;
      });
    } finally {
      setStreaming(false);
      setToolStatus(null);
      loadSessions();
      if (voiceModeRef.current) {
        voiceModeRef.current = false;
        if (fullText.trim()) speak(fullText, -1);
      }
    }
  };

  const firstName = (user?.name || "").split(" ")[0];
  const initials = (user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  const Sidebar = (
    <div className="h-full w-72 bg-navy text-cream flex flex-col">
      <div className="px-6 h-20 flex items-center border-b border-cream/10">
        <span className="font-sans tracking-[0.35em] text-lg">OMNIA</span>
        <span className="ml-2 mt-1 text-[9px] tracking-[0.2em] text-[#C9A96A]">INTELLIGENCE</span>
      </div>
      <div className="p-3">
        <button data-testid="omnia-new-session" onClick={newSession}
          className="w-full flex items-center gap-2.5 rounded-xl border border-cream/15 px-4 py-2.5 text-sm text-cream/80 hover:bg-cream/5 hover:text-cream transition-colors">
          <Plus className="w-4 h-4" /> Nueva conversación
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto thin-scroll px-3 space-y-1">
        {sessions.map((s) => (
          <div key={s.session_id} data-testid={`omnia-session-${s.session_id}`} onClick={() => openSession(s.session_id)}
            className={`group flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-colors
              ${s.session_id === sessionId ? "bg-cream/10 text-cream" : "text-cream/55 hover:bg-cream/5 hover:text-cream/85"}`}>
            <span className="flex-1 truncate">{s.title}</span>
            <button onClick={(e) => removeSession(s.session_id, e)} data-testid={`omnia-session-del-${s.session_id}`}
              className="opacity-0 group-hover:opacity-100 text-cream/40 hover:text-rose-400 transition-all">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-cream/10 space-y-1">
        <button onClick={enrollPasskey} data-testid="omnia-passkey-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-cream/60 hover:text-cream hover:bg-cream/5 transition-colors">
          <Fingerprint className="w-4 h-4" /> Passkey (huella / Face ID)
        </button>
        <Link to="/os" data-testid="omnia-classic-link"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-cream/60 hover:text-cream hover:bg-cream/5 transition-colors">
          <LayoutDashboard className="w-4 h-4" /> Vista clásica
        </Link>
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6C5CE7] to-[#3d2fae] flex items-center justify-center text-white text-[11px] font-medium">{initials}</div>
          <div className="min-w-0 flex-1">
            <div className="text-sm truncate">{user?.name}</div>
            <div className="text-[11px] text-cream/40 truncate">{user?.role}</div>
          </div>
          <button data-testid="omnia-logout" onClick={logout} title="Cerrar sesión"
            className="text-cream/40 hover:text-cream transition-colors"><LogOut className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex bg-cream overflow-hidden" data-testid="omnia-chat-page">
      <OnboardingGate onReady={() => setHrReady(true)} />
      {hrReady && <DayStart mandatory />}
      <aside className="hidden lg:block shrink-0">{Sidebar}</aside>
      {sideOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={() => setSideOpen(false)} />
          <div className="absolute inset-y-0 left-0">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col relative">
        <div className="pointer-events-none absolute inset-0 opacity-60"
          style={{ background: "radial-gradient(1000px 500px at 80% -10%, rgba(108,92,231,0.08), transparent), radial-gradient(800px 400px at 10% 110%, rgba(201,169,106,0.08), transparent)" }} />

        <header className="relative h-16 flex items-center gap-3 px-5 border-b border-navy/5 bg-cream/70 backdrop-blur-xl z-10">
          <button className="lg:hidden p-2 -ml-2 text-navy/60" onClick={() => setSideOpen(true)} data-testid="omnia-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <span className="relative flex w-2.5 h-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span className="text-sm text-navy/60 truncate">OMNIA conectado a todos tus módulos</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <TenantSwitcher />
            <ChatBell />
            <NotificationBell />
          </div>
        </header>

        <div ref={scrollRef} data-testid="omnia-chat-messages" className="relative flex-1 overflow-y-auto thin-scroll z-10">
          <div className="max-w-3xl mx-auto px-5 py-8">
            {messages.length === 0 ? (
              <div className="pt-[12vh] text-center">
                <Orb />
                <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}
                  className="mt-8 font-editorial text-4xl sm:text-5xl tracking-tight text-navy">
                  ¿Qué hacemos hoy{firstName ? `, ${firstName}` : ""}?
                </motion.h1>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.7 }}
                  className="mt-3 text-navy/50 max-w-md mx-auto text-base">
                  Pídeme datos, dashboards o acciones. Yo me encargo del resto.
                </motion.p>
                <div className="mt-10 grid sm:grid-cols-3 gap-3 text-left">
                  {SUGGESTIONS.map((s, i) => (
                    <motion.button key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + i * 0.07, duration: 0.6, ease }}
                      data-testid={`omnia-suggestion-${i}`} onClick={() => send(s.q)}
                      className="group rounded-2xl border border-navy/10 bg-white/70 backdrop-blur p-4 hover:border-[#6C5CE7]/40 hover:-translate-y-0.5 transition-all text-left">
                      <div className="text-sm font-medium text-navy group-hover:text-[#6C5CE7] transition-colors">{s.t}</div>
                      <div className="mt-1 text-xs text-navy/45 line-clamp-2">{s.q}</div>
                    </motion.button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-7 pb-6">
                {messages.map((m, i) => (
                  <div key={i}>
                    {m.role === "user" ? (
                      <div className="flex justify-end">
                        <div className="max-w-[85%]">
                          {(m.attachments || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 justify-end mb-1.5">
                              {m.attachments.map((a, k) => (
                                <span key={k} className="inline-flex items-center gap-1.5 rounded-full bg-navy/8 border border-navy/10 px-3 py-1 text-xs text-navy/70">
                                  <FileText className="w-3 h-3" /> {a.filename}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="rounded-2xl rounded-br-md bg-navy text-cream px-5 py-3 text-[0.95rem] leading-relaxed whitespace-pre-wrap">
                            {m.content}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 rounded-full"
                            style={m.director ? { background: m.director.color } : { background: "linear-gradient(135deg, #6C5CE7, #3d2fae)" }} />
                          {m.director ? (
                            <>
                              <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-white"
                                style={{ background: m.director.color }} data-testid="omnia-director-badge">{m.director.short}</span>
                              <span className="text-[11px] tracking-[0.12em] text-navy/45 uppercase truncate">{m.director.name}</span>
                            </>
                          ) : (
                            <span className="text-[11px] tracking-[0.25em] text-navy/40">OMNIA</span>
                          )}
                        </div>
                        {(m.council || []).length > 0 && (
                          <div data-testid="omnia-council-strip" className="mb-3 rounded-2xl border border-navy/10 bg-white/70 backdrop-blur px-4 py-3">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-navy/40 mb-2">Consejo Directivo · Deliberación</div>
                            <div className="flex flex-wrap gap-1.5">
                              {m.council.map((d, k) => (
                                <span key={k} className="inline-flex items-center gap-1.5 rounded-full border bg-white px-2.5 py-1 text-[11px] font-medium"
                                  style={{ borderColor: `${d.color}55`, color: d.color }}>
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: d.color }} />
                                  {d.short}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {m.content && (
                          <div className="text-[0.95rem] leading-relaxed text-navy/85 space-y-0.5">{renderText(m.content)}</div>
                        )}
                        {m.content && !streaming && (
                          <button onClick={() => speak(m.content, i)} data-testid={`omnia-speak-${i}`}
                            title={speakingIdx === i ? "Detener" : "Escuchar respuesta"}
                            className={`mt-1.5 inline-flex items-center gap-1.5 text-[11px] transition-colors ${speakingIdx === i ? "text-[#6C5CE7]" : "text-navy/35 hover:text-[#6C5CE7]"}`}>
                            {speakingIdx === i ? <Square className="w-3 h-3" /> : <Volume2 className="w-3.5 h-3.5" />}
                            {speakingIdx === i ? "Detener" : "Escuchar"}
                          </button>
                        )}
                        {m.blocks.map((b, j) => (
                          <AiBlock key={j} block={b} onSend={send} disabled={streaming && i !== messages.length - 1} />
                        ))}
                        {i === messages.length - 1 && streaming && !m.content && !m.blocks.length && !toolStatus && (
                          <div className="flex items-center gap-2 text-navy/40 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Pensando…</div>
                        )}
                      </motion.div>
                    )}
                  </div>
                ))}
                <AnimatePresence>
                  {toolStatus && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      data-testid="omnia-tool-status"
                      className="inline-flex items-center gap-2.5 rounded-full border border-[#6C5CE7]/25 bg-[#6C5CE7]/[0.06] px-4 py-2 text-sm text-[#5a49d6]">
                      <span className="relative flex w-2 h-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#6C5CE7] opacity-60" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#6C5CE7]" />
                      </span>
                      {toolStatus}…
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

        <div className="relative z-10 px-5 pb-5">
          <div className="max-w-3xl mx-auto">
            {(attachments.length > 0 || uploading) && (
              <div className="flex flex-wrap items-center gap-1.5 mb-2 px-1" data-testid="omnia-attachments-bar">
                {attachments.map((a, i) => (
                  <span key={i} data-testid={`omnia-attachment-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#6C5CE7]/25 px-3 py-1.5 text-xs text-navy/75 shadow-sm">
                    <FileText className="w-3.5 h-3.5 text-[#6C5CE7]" />
                    <span className="max-w-[180px] truncate">{a.filename}</span>
                    <button onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))}
                      className="text-navy/35 hover:text-rose-500 transition-colors"><X className="w-3.5 h-3.5" /></button>
                  </span>
                ))}
                {uploading && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-navy/45">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Subiendo…
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 rounded-2xl border border-navy/10 bg-white/90 backdrop-blur-xl shadow-lg shadow-navy/5 px-4 py-2 focus-within:border-[#6C5CE7]/50 transition-colors">
              <input ref={fileRef} type="file" multiple className="hidden" data-testid="omnia-attach-input"
                accept=".pdf,.xml,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt" onChange={onFiles} />
              <button onClick={() => fileRef.current?.click()} disabled={streaming} data-testid="omnia-attach-btn"
                title="Adjuntar archivos"
                className="w-9 h-9 rounded-xl flex items-center justify-center text-navy/45 hover:text-[#6C5CE7] hover:bg-[#6C5CE7]/10 transition-colors disabled:opacity-40">
                <Paperclip className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
              </button>
              <button onClick={toggleRecording} disabled={streaming || transcribing} data-testid="omnia-mic-btn"
                title={recording ? "Detener grabación" : "Hablarle a OMNIA"}
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors disabled:opacity-40 ${
                  recording ? "bg-rose-500 text-white animate-pulse" : "text-navy/45 hover:text-[#6C5CE7] hover:bg-[#6C5CE7]/10"}`}>
                {transcribing ? <Loader2 style={{ width: 18, height: 18 }} className="animate-spin" />
                  : recording ? <Square style={{ width: 16, height: 16 }} /> : <Mic style={{ width: 18, height: 18 }} />}
              </button>
              <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                placeholder="Escríbele a OMNIA…" maxLength={2000} data-testid="omnia-chat-input"
                className="flex-1 bg-transparent outline-none text-[0.95rem] text-navy placeholder:text-navy/35 py-2" />
              <button onClick={() => send()} disabled={streaming || uploading || (!input.trim() && !attachments.length)} data-testid="omnia-chat-send"
                className="w-10 h-10 rounded-xl bg-navy text-cream flex items-center justify-center hover:bg-[#6C5CE7] transition-colors disabled:opacity-40 disabled:hover:bg-navy">
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <div className="mt-2 text-center text-[11px] text-navy/30">
              OMNIA consulta tus datos en tiempo real y puede ejecutar acciones por ti.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
